/**
 * Print Keychain Cards
 * Generates printable QR keychain cards for all enrolled children.
 * Card design: light mint background, stars/sun accents, logo top-left,
 * center name centered, QR code prominent, child name at bottom.
 * No rounded corners — sharp edges for clean cutting and laminating.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Printer, CheckSquare, Square, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Card color themes
const THEMES = [
  { id: "mint",   label: "Light Mint",    bg: "#e8fdf5", accent: "#0f766e", text: "#134e4a",  star: "#34d399" },
  { id: "sky",    label: "Sky Blue",      bg: "#e0f2fe", accent: "#0369a1", text: "#0c4a6e",  star: "#38bdf8" },
  { id: "lavender", label: "Soft Lavender", bg: "#f3e8ff", accent: "#7e22ce", text: "#4a044e", star: "#a855f7" },
  { id: "peach",  label: "Warm Peach",    bg: "#fff1e6", accent: "#c2410c", text: "#7c2d12",  star: "#fb923c" },
];

// Category filters — maps to category + fullTimeSubcategory DB fields
const FILTERS = [
  { id: "all",           label: "All" },
  { id: "prek",          label: "Pre-K" },
  { id: "older_toddler", label: "Older Toddler" },
  { id: "young_toddler", label: "Young Toddler" },
  { id: "infant",        label: "Infant" },
  { id: "part_time",     label: "Part Time" },
  { id: "school_age",    label: "School Age" },
  { id: "after_school",  label: "After School" },
];

const FT_SUBS = ["prek", "older_toddler", "young_toddler", "infant"];

function matchesFilter(child, filterId) {
  if (filterId === "all") return true;
  if (FT_SUBS.includes(filterId))
    return child.category === "full_time" && child.fullTimeSubcategory === filterId;
  return child.category === filterId;
}

// Star SVG accent
const StarAccent = ({ size = 12, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
  </svg>
);

// Sun SVG accent
const SunAccent = ({ size = 14, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="5" fill={color} stroke="none"/>
    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

function KeychainCard({ child, scanUrl, theme, orgName, logoUrl }) {
  const t = THEMES.find(th => th.id === theme) || THEMES[0];

  return (
    <div
      className="inline-block overflow-hidden"
      style={{
        width: "252px",   // 3.5 inches at 72dpi
        height: "144px",  // 2 inches at 72dpi
        backgroundColor: t.bg,
        border: `2px solid ${t.accent}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
        pageBreakInside: "avoid",
        position: "relative",
        flexShrink: 0,
      }}
      data-testid={`keychain-card-${child.childId}`}
    >
      {/* Star/sun corner accents */}
      <div style={{ position: "absolute", top: 5, left: 5, opacity: 0.55 }}>
        <StarAccent size={10} color={t.star} />
      </div>
      <div style={{ position: "absolute", top: 5, right: 34, opacity: 0.45 }}>
        <SunAccent size={10} color={t.star} />
      </div>
      <div style={{ position: "absolute", bottom: 5, left: 34, opacity: 0.45 }}>
        <SunAccent size={10} color={t.star} />
      </div>
      <div style={{ position: "absolute", bottom: 5, right: 5, opacity: 0.55 }}>
        <StarAccent size={10} color={t.star} />
      </div>

      {/* Header: logo + center name */}
      <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 8px 4px", borderBottom: `1.5px solid ${t.accent}30` }}>
        <img src={logoUrl} alt="logo" style={{ height: 22, width: 22, objectFit: "contain", flexShrink: 0 }} onError={e => e.target.style.display = "none"} />
        <p style={{ fontSize: 9, fontWeight: 700, color: t.accent, textAlign: "center", flex: 1, letterSpacing: "0.02em", textTransform: "uppercase", lineHeight: 1.2 }}>
          {orgName}
        </p>
        {/* Keychain hole placeholder */}
        <div style={{ width: 10, height: 10, borderRadius: "50%", border: `1.5px solid ${t.accent}60`, flexShrink: 0, marginRight: 2 }} />
      </div>

      {/* Body: QR + child name */}
      <div style={{ display: "flex", alignItems: "center", padding: "6px 8px", gap: 10, height: "calc(100% - 38px)" }}>
        {/* QR code */}
        <div style={{ flexShrink: 0, background: "#fff", padding: 3, border: `1px solid ${t.accent}30` }}>
          <QRCodeSVG value={scanUrl} size={72} bgColor="#ffffff" fgColor="#0f172a" level="M" />
        </div>
        {/* Child info */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          <p style={{ fontSize: 12, fontWeight: 800, color: t.text, lineHeight: 1.2, marginBottom: 4, wordBreak: "break-word" }}>
            {child.childName}
          </p>
          {child.className && (
            <p style={{ fontSize: 8, color: t.accent, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              {child.className}
            </p>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 6 }}>
            <StarAccent size={7} color={t.star} />
            <p style={{ fontSize: 7, color: t.accent, fontWeight: 600 }}>Scan to Sign In / Out</p>
            <StarAccent size={7} color={t.star} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PrintKeychainCardsPage() {
  const { organization } = useAuth();
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState("mint");
  const [activeFilter, setActiveFilter] = useState("all");
  const orgName = organization?.name || "Center Name";
  const logoUrl = `${BACKEND_URL}/simple-sign-in-logo.png`;
  const appUrl = BACKEND_URL.replace("/api", "");

  useEffect(() => {
    axios.get(`${API}/qr-tokens/children-all`)
      .then(r => {
        const kids = r.data.children || [];
        setChildren(kids);
        setSelected(new Set(kids.map(c => c.childId)));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // When filter changes, auto-check all children matching that filter
  useEffect(() => {
    if (children.length === 0) return;
    const matching = children.filter(c => matchesFilter(c, activeFilter));
    setSelected(new Set(matching.map(c => c.childId)));
  }, [activeFilter, children]);

  const toggleChild = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filteredChildren = children.filter(c => matchesFilter(c, activeFilter));

  const toggleAll = () => {
    const allFilteredIds = filteredChildren.map(c => c.childId);
    const allChecked = allFilteredIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allChecked) {
        allFilteredIds.forEach(id => next.delete(id));
      } else {
        allFilteredIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const handlePrint = () => window.print();

  const visible = children.filter(c => selected.has(c.childId));

  return (
    <AppShell pageTitle="Print Keychain Cards" breadcrumb>
      {/* Controls — hidden during print */}
      <div className="print:hidden mb-6 space-y-4">
        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 p-5 text-white">
          <h1 className="text-xl font-bold mb-1">Keychain Sign-In Cards</h1>
          <p className="text-teal-100 text-sm">Each card contains a unique QR code. Print, cut, laminate, and attach to a keychain. Parents scan to sign their child in or out.</p>
        </div>

        {/* Theme selector */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-slate-600">Card Color:</span>
          {THEMES.map(t => (
            <button key={t.id} onClick={() => setTheme(t.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-sm font-semibold transition ${theme === t.id ? "border-teal-500 shadow-sm" : "border-slate-200"}`}
              style={{ backgroundColor: t.bg, color: t.text }}>
              <span style={{ width: 12, height: 12, borderRadius: "50%", background: t.accent, flexShrink: 0 }} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Child selector */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {/* Category filter bar */}
          <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
            {FILTERS.map(f => {
              const count = children.filter(c => matchesFilter(c, f.id)).length;
              if (f.id !== "all" && count === 0) return null;
              return (
                <button
                  key={f.id}
                  onClick={() => setActiveFilter(f.id)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
                    activeFilter === f.id
                      ? "bg-teal-600 text-white border-teal-600"
                      : "bg-white text-slate-600 border-slate-300 hover:border-teal-400"
                  }`}
                  data-testid={`filter-${f.id}`}
                >
                  {f.label} ({count})
                </button>
              );
            })}
          </div>
          {/* Select/Deselect all row */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <button onClick={toggleAll} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              {filteredChildren.length > 0 && filteredChildren.every(c => selected.has(c.childId))
                ? <CheckSquare className="h-4 w-4 text-teal-600" />
                : <Square className="h-4 w-4 text-slate-400" />}
              {filteredChildren.length > 0 && filteredChildren.every(c => selected.has(c.childId)) ? "Deselect All" : "Select All"}
            </button>
            <span className="text-xs text-slate-400">{selected.size} of {children.length} selected</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-100">
            {filteredChildren.map(c => (
              <button key={c.childId} onClick={() => toggleChild(c.childId)}
                className={`flex items-center gap-2 px-3 py-2.5 bg-white text-left text-sm transition hover:bg-teal-50 ${selected.has(c.childId) ? "text-teal-800 font-semibold" : "text-slate-500"}`}
                data-testid={`select-child-${c.childId}`}>
                {selected.has(c.childId) ? <CheckSquare className="h-4 w-4 text-teal-600 flex-shrink-0" /> : <Square className="h-4 w-4 text-slate-300 flex-shrink-0" />}
                <span className="truncate">{c.childName}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={handlePrint} disabled={selected.size === 0}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6"
            data-testid="print-cards-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print {selected.size} Card{selected.size !== 1 ? "s" : ""}
          </Button>
          <p className="text-xs text-slate-400">6 cards per page — cut along edges — laminate — punch hole in circle</p>
        </div>
      </div>

      {/* Loading */}
      {loading && <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div>}

      {/* Print sheet — 6 cards per row of 2, 3 rows per page */}
      {!loading && (
        <div
          className="print-sheet"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            padding: "0",
          }}
          data-testid="cards-sheet"
        >
          {visible.map(child => (
            <KeychainCard
              key={child.childId}
              child={child}
              scanUrl={`${appUrl}/qr-signin/${child.qrToken}`}
              theme={theme}
              orgName={orgName}
              logoUrl={logoUrl}
            />
          ))}
        </div>
      )}

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: letter landscape; margin: 0.4in; }
          .print\\:hidden { display: none !important; }
          .print-sheet { gap: 10px !important; }
          nav, header, aside, [data-testid="app-shell-sidebar"] { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </AppShell>
  );
}
